import { act, fireEvent, screen, waitFor, within } from "@testing-library/react";
import { userEvent } from "@testing-library/user-event";

import { isDefined } from "@eroad/helpers/isDefined";
import { isNil } from "@eroad/helpers/isNil";
import type { MaybeNull, Nullable } from "@eroad/types";

import {
  isAltSelector,
  isLabelSelector,
  isPlaceholderSelector,
  isRoleSelector,
  isTextSelector,
  isTitleSelector,
  isValueSelector,
} from "./helpers";
import type { DriverOptions } from "./types";

/**
 * This is the base class for all the drivers.
 */
class Driver<T extends HTMLElement | SVGSVGElement, R = T extends SVGSVGElement ? SVGSVGElement : T> {
  protected container: Nullable<HTMLElement>;
  protected selector: NonNullable<DriverOptions>;

  protected act = act;
  protected fireEvent = fireEvent;
  protected screen = screen;
  protected user = userEvent.setup();
  protected within = within;
  protected waitFor = waitFor;

  constructor(selector: NonNullable<DriverOptions>, container?: Nullable<HTMLElement>) {
    this.container = container;
    this.selector = selector;
  }

  /**
   * Use this method to get the element in the DOM.
   * If the element is not present, it will return null.
   *
   * @example
   * ```ts
   * expect(driver.getElement()).toBeInTheDocument();
   * expect(driver.getElement()).not.toBeInTheDocument();
   * expect(driver.getElement()).toHaveTextContent("Hello World!");
   * expect(driver.getElement()).to* (see jest or testing-library docs for more matchers)
   * ```
   *
   * @see
   * [jest matchers](https://jestjs.io/docs/expect)
   *
   * @see
   * [testing-library matchers](https://github.com/testing-library/jest-dom?tab=readme-ov-file#custom-matchers)
   */
  getElement(): MaybeNull<R> {
    if (isAltSelector(this.selector)) {
      return this.wrapper.queryByAltText(this.selector.alt, this.selector.options) as R;
    }

    if (isLabelSelector(this.selector)) {
      return this.wrapper.queryByLabelText(this.selector.label, this.selector.options) as R;
    }

    if (isPlaceholderSelector(this.selector)) {
      return this.wrapper.queryByPlaceholderText(this.selector.placeholder, this.selector.options) as R;
    }

    if (isRoleSelector(this.selector)) {
      return this.wrapper.queryByRole(this.selector.role, this.selector.options) as R;
    }

    if (isTextSelector(this.selector)) {
      return this.wrapper.queryByText(this.selector.text, this.selector.options) as R;
    }

    if (isTitleSelector(this.selector)) {
      return this.wrapper.queryByTitle(this.selector.title, this.selector.options) as R;
    }

    if (isValueSelector(this.selector)) {
      return this.wrapper.queryByDisplayValue(this.selector.value, this.selector.options) as R;
    }

    return this.wrapper.queryByTestId(this.selector) as R;
  }

  /**
   * Use this method to wait for the element to appear in the DOM.
   *
   * @example
   * ```ts
   * const element = await driver.waitForElement();
   *
   * expect(element).toBeInTheDocument();
   * ```
   */
  waitForElement(): Promise<R> {
    if (isAltSelector(this.selector)) {
      return this.wrapper.findByAltText(this.selector.alt, this.selector.options) as Promise<R>;
    }

    if (isLabelSelector(this.selector)) {
      return this.wrapper.findByLabelText(this.selector.label, this.selector.options) as Promise<R>;
    }

    if (isPlaceholderSelector(this.selector)) {
      return this.wrapper.findByPlaceholderText(this.selector.placeholder, this.selector.options) as Promise<R>;
    }

    if (isRoleSelector(this.selector)) {
      return this.wrapper.findByRole(this.selector.role, this.selector.options) as Promise<R>;
    }

    if (isTextSelector(this.selector)) {
      return this.wrapper.findByText(this.selector.text, this.selector.options) as Promise<R>;
    }

    if (isTitleSelector(this.selector)) {
      return this.wrapper.findByTitle(this.selector.title, this.selector.options) as Promise<R>;
    }

    if (isValueSelector(this.selector)) {
      return this.wrapper.findByDisplayValue(this.selector.value, this.selector.options) as Promise<R>;
    }

    return this.wrapper.findByTestId(this.selector) as Promise<R>;
  }

  /**
   * Gets the button with the specified text.
   *
   * @param text - The text content of the button.
   *
   * @example
   * ```ts
   * const button = this.getButtonByText("Today");
   * ```
   */
  protected getButtonByText(name: string): MaybeNull<HTMLButtonElement> {
    return this.wrapper.queryByRole<HTMLButtonElement>("button", { name });
  }

  /**
   * Gets the button with the specified class name.
   *
   * @remarks
   * Use 'getButtonByText' instead. Use this method only as a last resort when the text is not known.
   *
   * @param className - The class name of the button.
   *
   * @throws
   * If the button is not found.
   *
   * @example
   * ```ts
   * const button = this.getButtonByClassName("ant-picker-month-btn");
   * ```
   */
  protected getButtonByClassName(className: string): MaybeNull<HTMLButtonElement> {
    const buttonElement = this.wrapper
      .queryAllByRole<HTMLButtonElement>("button")
      .find(({ classList }) => classList.contains(className));

    return buttonElement ?? null;
  }

  /**
   * This is to get the element bypassing the null check.
   * Use only inside Drivers.
   *
   * @throws
   * If the element is not found.
   */
  protected get element(): R {
    const element = this.getElement();

    if (isNil(element)) {
      this.throwNotFoundError("Element");
    }

    return element;
  }

  /**
   * It allows the querying of the element inside the container.
   * Default to `screen` if container is not set.
   */
  protected get wrapper() {
    if (isDefined(this.container)) {
      return this.within(this.container);
    }

    return this.screen;
  }

  /**
   * Throws an error indicating that the specified label was not found.
   *
   * @param label - The label of the element that was not found.
   *
   * @example
   * ```ts
   * this.throwNotFoundError("Submit Button");
   * // Throws: "Submit Button not found."
   * ```
   */
  protected throwNotFoundError(label: string): never {
    throw new Error(`${label} not found.`);
  }
}

export { Driver };
